import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Icon from '../../Icon';
import clsx from 'clsx';
import { translate } from '../../../Services/translation';
import { CustomWallpaperInfo } from '../../../Types/ProductPageModel';
import EditorString from '../../EditorString/EditorString';
import { triggerToast } from '../../../Services/Toast.service';

export interface CustomWallpaperFile {
  file: File;
  width: number;
  height: number;
  getUrl: () => string;
}

interface CustomWallpaperDropzoneProps {
  onFileSelected: (customWallpaperFile: CustomWallpaperFile) => void;
  isReplacementZone?: boolean;
  selectedImageUrl?: string;
  customWallpaperInfo: CustomWallpaperInfo;
}

const validateFile = (
  customWallpaperFile: CustomWallpaperFile,
  customWallpaperInfo: CustomWallpaperInfo
) => {
  if (customWallpaperFile.file.size > customWallpaperInfo.maxSizeBytes) {
    throw new Error(
      translate('measured-wallpaper.custom.file-too-large').replace(
        '{MaxSize}',
        customWallpaperInfo.maxSizeFormatted
      )
    );
  }

  if (
    customWallpaperFile.width < customWallpaperInfo.minWidth ||
    customWallpaperFile.height < customWallpaperInfo.minHeight
  ) {
    throw new Error(
      translate('measured-wallpaper.custom.image-too-small')
        .replace('{Width}', customWallpaperInfo.minWidth.toString())
        .replace('{Height}', customWallpaperInfo.minHeight.toString())
    );
  }
};

const CustomWallpaperDropzone = ({
  onFileSelected,
  isReplacementZone = false,
  selectedImageUrl,
  customWallpaperInfo,
}: CustomWallpaperDropzoneProps) => {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length == 0) return;

    const formData = new FormData();
    acceptedFiles.map((file, idx) => {
      formData.append('imageUpload' + idx, file);
      const _URL = window.URL || window.webkitURL;
      const img = new Image();
      const objectUrl = _URL.createObjectURL(file);
      img.addEventListener('load', (e) => {
        var loadedImage = e.target as HTMLImageElement;

        const customWallpaperFile: CustomWallpaperFile = {
          file,
          width: loadedImage.width,
          height: loadedImage.height,
          getUrl: () => _URL.createObjectURL(file),
        };

        try {
          validateFile(customWallpaperFile, customWallpaperInfo);
          onFileSelected(customWallpaperFile);
        } catch (err) {
          console.log(err);
          triggerToast({
            title: translate(
              'measured-wallpaper.custom.error-toast-title'
            ),
            text: err.message,
            type: 'error',
            autoClose: false
          });
        }

        _URL.revokeObjectURL(objectUrl);
        img.remove();
      });
      img.src = objectUrl;
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: customWallpaperInfo.acceptedFileTypes.reduce(
      (prev, fileType) => ({ ...prev, [fileType]: [] }),
      {}
    ),
    minSize: 0,
    maxSize: customWallpaperInfo.maxSizeBytes,
    multiple: false,
  });

  return (
    <div
      className={clsx(
        'relative mx-auto w-full max-w-3xl overflow-hidden rounded border-2 border-dashed border-gray-200 text-center'
      )}
      {...getRootProps()}
    >
      {selectedImageUrl && (
        <div
          className="absolute inset-0 z-0 h-full w-full rounded"
          style={{
            backgroundImage: `url(${selectedImageUrl})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
        ></div>
      )}
      <div
        className={clsx(
          'flex w-full cursor-pointer flex-col items-center justify-center rounded px-2 pb-4 pt-3 hover:bg-success-100',
          isDragActive ? 'bg-success-100' : 'bg-success-50',
          selectedImageUrl && 'opacity-[0.85]',
          !isReplacementZone && 'h-64'
        )}
      >
        <input {...getInputProps()} type="file" />
        {!isReplacementZone && (
          <Icon
            name={selectedImageUrl ? 'check' : 'image'}
            className="h-14 w-14"
          />
        )}
        {!isReplacementZone && (
          <div className="text-md text-gray-700">
            {!selectedImageUrl && (
              <EditorString {...customWallpaperInfo.infoText} />
            )}
            {selectedImageUrl && (
              <>
                <p className="font-bold">
                  {selectedImageUrl &&
                    translate('measured-wallpaper.custom.image-selected')}
                </p>
                <p>
                  {selectedImageUrl &&
                    translate(
                      'measured-wallpaper.custom.choose-wall-sizes'
                    )}
                </p>
              </>
            )}
          </div>
        )}

        {isReplacementZone && <Icon name="images" className="h-14 w-14" />}
        {isReplacementZone && (
          <div className="text-md text-gray-700">
            <p className="font-bold">
              {translate('measured-wallpaper.custom.change-image')}
            </p>
            <p>{translate('measured-wallpaper.custom.drag-and-drop')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomWallpaperDropzone;
